import { createContext, type FC, type PropsWithChildren, useContext, useEffect, useState } from "react";
import { useMatches } from "react-router";

/**
 * Route `handle` the shell reads: full-bleed pages (maps) get the whole content area, no padding;
 * full-width pages (wide data grids) keep the padding but drop the content's max width.
 */
export interface RouteHandle {
	fullBleed?: boolean;
	fullWidth?: boolean;
}

const useRouteHandle = (key: keyof RouteHandle) =>
	useMatches().some(match => (match.handle as RouteHandle | undefined)?.[key]);

export const useIsFullBleed = () => useRouteHandle("fullBleed");

export const useIsFullWidth = () => useRouteHandle("fullWidth");

interface ShellState {
	/** The solution the page is about, for the top bar's solution pill (Figma D05–D14). */
	solutionId?: string;
	setSolutionId: (solutionId: string | undefined) => void;
}

const ShellContext = createContext<ShellState>({ setSolutionId: () => undefined });

export const ShellProvider: FC<PropsWithChildren> = ({ children }) => {
	const [solutionId, setSolutionId] = useState<string>();
	return <ShellContext value={{ solutionId, setSolutionId }}>{children}</ShellContext>;
};

export const useShellSolutionId = () => useContext(ShellContext).solutionId;

/** Declares the page's solution; the top bar shows its pill while the page is mounted. */
export const useSolutionContext = (solutionId: string | undefined) => {
	const { setSolutionId } = useContext(ShellContext);
	useEffect(() => {
		setSolutionId(solutionId);
		return () => setSolutionId(undefined);
	}, [setSolutionId, solutionId]);
};
